import {
  decodePolyline,
  haversineKm,
  slicePlanSegments,
  type RouteDetail,
  type RouteGravel,
  type RoutePlan,
  type SegmentMode,
} from '@motogo/shared';
import { createContext, useCallback, useContext, useMemo, useRef, useState, type FC, type ReactNode } from 'react';

import { fetchRoute } from '@/lib/routing';

/**
 * State machine for the M3 waypoint route editor (see the saved-routes plan). The map is
 * the editing surface: RouteEditorPage (the /routes/new + /routes/:id/edit overlay) and
 * MapView both talk to this provider — the same map-bridge pattern as useRideReview.
 *
 * The core economy: one stateless `POST /route` per routed segment, and only for segments
 * an edit actually touches (move waypoint i → re-route segments i−1 and i, nothing else).
 * 'straight' segments never call the router — a two-point line with Haversine distance.
 * Reopening a saved route slices its stored polyline via the plan's endIndex marks — zero
 * routing calls until something changes.
 *
 * 'track' segments (editing GPX/ride routes) never call the router either: they carry
 * verbatim stored geometry. A plan-less route hydrates as start/finish waypoints around
 * one track segment; splitting a track cuts it at the nearest polyline vertex (geometry
 * unchanged, still zero calls), and only re-routing a stretch — dragging one of its
 * endpoints or switching its mode chip — replaces that stretch's line.
 */

export interface EditorWaypoint {
  /** Session-local id (React keys, map hit-testing) — not persisted. */
  id: string;
  lat: number;
  lon: number;
  name?: string;
}

export interface EditorSegment {
  /** Session-local id, stable across re-routes so selection survives a recalc. */
  id: string;
  mode: SegmentMode;
  gravel: RouteGravel;
  /** [lon, lat] GeoJSON order, both endpoints included. Beeline placeholder while routing. */
  coords: [number, number][];
  distanceMeters: number;
  /** Router-reported riding time; null for straight segments (estimated at save time). */
  durationMillis: number | null;
  status: 'ok' | 'routing' | 'error';
}

export type EditorSelection = { kind: 'waypoint'; id: string } | { kind: 'segment'; id: string } | null;

/** What a save persists — geometry concatenated from the segments plus the plan recipe. */
export interface EditorBuildResult {
  /** [lon, lat] pairs, segment joints deduped. */
  coordinates: [number, number][];
  plan: RoutePlan;
  durationSeconds: number;
  distanceMeters: number;
}

interface EditorModel {
  active: boolean;
  /** Set when editing an existing route — the PUT target and name/description to keep. */
  editing: { id: string; name: string; description: string | null } | null;
  waypoints: EditorWaypoint[];
  segments: EditorSegment[];
  selection: EditorSelection;
  /** Mode/gravel for the segment the next added waypoint will create. */
  nextMode: SegmentMode;
  nextGravel: RouteGravel;
  dirty: boolean;
  undoDepth: number;
}

// Server-enforced caps mirrored client-side (RoutePlanValidator).
export const MAX_WAYPOINTS = 200;
export const MAX_VERBATIM_POINTS = 10_000;
const UNDO_CAP = 50;

// Straight segments carry no router duration; estimate at a back-road cruising pace.
const STRAIGHT_MPS = 50 / 3.6;

const EMPTY: EditorModel = {
  active: false,
  editing: null,
  waypoints: [],
  segments: [],
  selection: null,
  nextMode: 'fast',
  nextGravel: 'neutral',
  dirty: false,
  undoDepth: 0,
};

/** Seconds for a segment: router value when it has one, 50 km/h estimate otherwise. */
function segmentSeconds(segment: EditorSegment): number {
  return segment.durationMillis != null ? segment.durationMillis / 1000 : segment.distanceMeters / STRAIGHT_MPS;
}

function beelineCoords(from: EditorWaypoint, to: EditorWaypoint): [number, number][] {
  return [
    [from.lon, from.lat],
    [to.lon, to.lat],
  ];
}

function lineMeters(coords: [number, number][]): number {
  let meters = 0;
  for (let i = 1; i < coords.length; i++) {
    meters += haversineKm(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0]) * 1000;
  }
  return meters;
}

/** Index of the polyline vertex nearest to (lat, lon) — where a track segment splits. */
function nearestVertexIndex(coords: [number, number][], lat: number, lon: number): number {
  let best = 0;
  let bestKm = Infinity;
  for (let i = 0; i < coords.length; i++) {
    const km = haversineKm(lat, lon, coords[i][1], coords[i][0]);
    if (km < bestKm) {
      bestKm = km;
      best = i;
    }
  }
  return best;
}

interface RouteEditorValue {
  active: boolean;
  editing: EditorModel['editing'];
  waypoints: EditorWaypoint[];
  segments: EditorSegment[];
  selection: EditorSelection;
  nextMode: SegmentMode;
  nextGravel: RouteGravel;
  dirty: boolean;
  canUndo: boolean;
  /** Any segment still waiting on the router. */
  busy: boolean;
  /** Any segment whose routing failed (save blocked until retried or changed). */
  hasError: boolean;
  totalDistanceMeters: number;
  totalDurationSeconds: number;
  canSave: boolean;

  startNew: (mode: SegmentMode, gravel: RouteGravel) => void;
  /** Hydrates from a saved waypoints-plan route — zero routing calls. False if the plan doesn't
   *  fit. The fallbacks seed the next-segment chips where the plan has nothing to inherit. */
  startFromRoute: (detail: RouteDetail, fallbackMode?: SegmentMode, fallbackGravel?: RouteGravel) => boolean;
  /** Hydrates from an unsaved route on the map (planner result / GPX / inline share) — same
   *  slicing as startFromRoute, but there's nothing to PUT onto: saving POSTs a new route.
   *  Non-waypoints plans are ignored (the line hydrates as a track). */
  startFromGeometry: (
    coordinates: [number, number][],
    plan: RoutePlan | null,
    durationSeconds: number | null,
    fallbackMode?: SegmentMode,
    fallbackGravel?: RouteGravel,
  ) => boolean;
  stop: () => void;
  addWaypoint: (lat: number, lon: number, name?: string) => void;
  moveWaypoint: (id: string, lat: number, lon: number) => void;
  removeWaypoint: (id: string) => void;
  /** Splits the segment at its midpoint into two (both inherit mode/gravel) — the tap-to-insert fallback. */
  insertVia: (segmentId: string) => void;
  /** Splits the segment at an explicit point — drag-on-line drops a via where the finger lands. */
  insertViaAt: (segmentId: string, lat: number, lon: number) => void;
  setSegmentMode: (segmentId: string, mode: SegmentMode) => void;
  setSegmentGravel: (segmentId: string, gravel: RouteGravel) => void;
  setNextMode: (mode: SegmentMode) => void;
  setNextGravel: (gravel: RouteGravel) => void;
  retrySegment: (segmentId: string) => void;
  /** Flips the route end-for-end: waypoints reverse, routed segments re-route (one-ways!),
   *  straight/track segments just reverse their line. */
  reverse: () => void;
  select: (selection: EditorSelection) => void;
  undo: () => void;
  /** Assembles the save payload (null unless saveable). Pure — reads current state. */
  buildResult: () => EditorBuildResult | null;
}

const RouteEditorContext = createContext<RouteEditorValue | null>(null);

export const RouteEditorProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [model, setModelState] = useState<EditorModel>(EMPTY);
  // Authoritative copy for the stable action callbacks and async routing completions —
  // the codebase's usual ref-mirror pattern, so MapView's handler effect never churns.
  const modelRef = useRef<EditorModel>(EMPTY);
  const undoRef = useRef<{ waypoints: EditorWaypoint[]; segments: EditorSegment[] }[]>([]);
  // Per-segment routing bookkeeping: a token invalidates responses that were superseded
  // (by a newer edit or an undo), the controller aborts the wire request itself.
  const tokenRef = useRef(new Map<string, number>());
  const abortRef = useRef(new Map<string, AbortController>());

  const setModel = useCallback((next: EditorModel) => {
    const withDepth = { ...next, undoDepth: undoRef.current.length };
    modelRef.current = withDepth;
    setModelState(withDepth);
  }, []);

  const abortAll = useCallback(() => {
    for (const ctrl of abortRef.current.values()) ctrl.abort();
    abortRef.current.clear();
    // Bump every token so in-flight completions that already cleared their abort check drop out.
    for (const [id, token] of tokenRef.current) tokenRef.current.set(id, token + 1);
  }, []);

  // Segments never mutate in place (every change replaces the segment object), so undo
  // snapshots are plain reference copies — deep enough, and free.
  const pushUndo = useCallback(() => {
    const m = modelRef.current;
    undoRef.current.push({ waypoints: m.waypoints, segments: m.segments });
    if (undoRef.current.length > UNDO_CAP) undoRef.current.shift();
  }, []);

  const launchRoute = useCallback(
    (segmentId: string, from: EditorWaypoint, to: EditorWaypoint, mode: Exclude<SegmentMode, 'straight' | 'track'>, gravel: RouteGravel) => {
      const token = (tokenRef.current.get(segmentId) ?? 0) + 1;
      tokenRef.current.set(segmentId, token);
      abortRef.current.get(segmentId)?.abort();
      const ctrl = new AbortController();
      abortRef.current.set(segmentId, ctrl);

      fetchRoute({ from: { lat: from.lat, lon: from.lon }, to: { lat: to.lat, lon: to.lon }, mode, gravel }, ctrl.signal)
        .then((route) => {
          if (tokenRef.current.get(segmentId) !== token) return; // superseded
          const geometry = route.geojson.features.find((f) => f.geometry.type === 'LineString')?.geometry;
          if (geometry?.type !== 'LineString') return;
          const coords = geometry.coordinates as [number, number][];
          const m = modelRef.current;
          if (!m.segments.some((s) => s.id === segmentId)) return; // segment removed meanwhile
          setModel({
            ...m,
            segments: m.segments.map((s) =>
              s.id === segmentId
                ? {
                    ...s,
                    coords,
                    distanceMeters: route.distanceMeters,
                    durationMillis: route.durationMillis,
                    status: 'ok' as const,
                  }
                : s,
            ),
          });
        })
        .catch(() => {
          if (ctrl.signal.aborted || tokenRef.current.get(segmentId) !== token) return;
          const m = modelRef.current;
          if (!m.segments.some((s) => s.id === segmentId)) return;
          setModel({
            ...m,
            segments: m.segments.map((s) => (s.id === segmentId ? { ...s, status: 'error' as const } : s)),
          });
        });
    },
    [setModel],
  );

  /** Fresh segment between two waypoints: straight resolves synchronously, else a routing placeholder. */
  const makeSegment = useCallback(
    (id: string, from: EditorWaypoint, to: EditorWaypoint, mode: SegmentMode, gravel: RouteGravel): EditorSegment => {
      const coords = beelineCoords(from, to);
      return {
        id,
        mode,
        gravel,
        coords,
        distanceMeters: lineMeters(coords),
        durationMillis: null,
        status: mode === 'straight' ? 'ok' : 'routing',
      };
    },
    [],
  );

  /**
   * Kicks off routing for exactly the listed segments (the ones an edit touched) — an
   * unrelated segment still in flight keeps its request. Straight/settled ones are skipped.
   */
  const routePending = useCallback(
    (waypoints: EditorWaypoint[], segments: EditorSegment[], ids: string[]) => {
      segments.forEach((segment, i) => {
        if (!ids.includes(segment.id) || segment.status !== 'routing') return;
        if (segment.mode === 'straight' || segment.mode === 'track') return;
        launchRoute(segment.id, waypoints[i], waypoints[i + 1], segment.mode, segment.gravel);
      });
    },
    [launchRoute],
  );

  const startNew = useCallback(
    (mode: SegmentMode, gravel: RouteGravel) => {
      abortAll();
      undoRef.current = [];
      setModel({ ...EMPTY, active: true, nextMode: mode, nextGravel: gravel });
    },
    [abortAll, setModel],
  );

  /** Shared hydration: a decoded line + optional waypoints plan → a live editor session
   *  (zero routing calls). `editing` set = saves PUT onto that route; null = saves POST. */
  const hydrate = useCallback(
    (
      coords: [number, number][],
      plan: RoutePlan | null,
      durationSeconds: number | null,
      editing: EditorModel['editing'],
      fallbackMode: SegmentMode,
      fallbackGravel: RouteGravel,
    ): boolean => {
      // Plan-less line (GPX import, ride conversion): hydrate as start/finish waypoints
      // around a single 'track' segment carrying the stored line verbatim. The first edit
      // gesture (tap/drag the line) splits it — that's how vias appear on a track.
      if (!plan) {
        if (coords.length < 2) return false;
        const [startCoord, endCoord] = [coords[0], coords[coords.length - 1]];
        const waypoints: EditorWaypoint[] = [
          { id: crypto.randomUUID(), lat: startCoord[1], lon: startCoord[0] },
          { id: crypto.randomUUID(), lat: endCoord[1], lon: endCoord[0] },
        ];
        abortAll();
        undoRef.current = [];
        setModel({
          ...EMPTY,
          active: true,
          nextMode: fallbackMode,
          nextGravel: fallbackGravel,
          editing,
          waypoints,
          segments: [
            {
              id: crypto.randomUUID(),
              mode: 'track',
              gravel: 'neutral',
              coords,
              distanceMeters: lineMeters(coords),
              durationMillis: durationSeconds != null ? durationSeconds * 1000 : null,
              status: 'ok',
            },
          ],
        });
        return true;
      }

      const slices = slicePlanSegments(coords, plan);
      if (!slices || plan.kind !== 'waypoints' || slices.length !== plan.waypoints.length - 1) return false;

      const waypoints: EditorWaypoint[] = plan.waypoints.map((w) => ({
        id: crypto.randomUUID(),
        lat: w.lat,
        lon: w.lon,
        name: w.name,
      }));

      // The stored plan has no per-segment durations — distribute the route total over
      // the routed slices proportionally to distance (straight ones re-estimate anyway).
      const distances = slices.map((s) => lineMeters(s.coords));
      const straightSeconds = slices.reduce((sum, s, i) => (s.mode === 'straight' ? sum + distances[i] / STRAIGHT_MPS : sum), 0);
      const routedMeters = slices.reduce((sum, s, i) => (s.mode === 'straight' ? sum : sum + distances[i]), 0);
      const routedSeconds = durationSeconds != null ? Math.max(0, durationSeconds - straightSeconds) : null;

      const segments: EditorSegment[] = slices.map((slice, i) => ({
        id: crypto.randomUUID(),
        mode: slice.mode,
        gravel: slice.gravel,
        coords: slice.coords,
        distanceMeters: distances[i],
        durationMillis:
          slice.mode === 'straight' || routedSeconds === null || routedMeters === 0
            ? null
            : (routedSeconds * distances[i]) / routedMeters * 1000,
        status: 'ok',
      }));

      abortAll();
      undoRef.current = [];
      const lastSegment = segments[segments.length - 1];
      setModel({
        ...EMPTY,
        active: true,
        editing,
        waypoints,
        segments,
        // Seed the next-segment chips from the last segment — extending keeps its feel.
        // A track can't be extended as a track (nothing to route), so it seeds the default.
        nextMode: lastSegment && lastSegment.mode !== 'track' ? lastSegment.mode : fallbackMode,
        nextGravel: lastSegment ? lastSegment.gravel : fallbackGravel,
      });
      return true;
    },
    [abortAll, setModel],
  );

  const startFromRoute = useCallback(
    (detail: RouteDetail, fallbackMode: SegmentMode = EMPTY.nextMode, fallbackGravel: RouteGravel = EMPTY.nextGravel): boolean => {
      if (detail.plan && detail.plan.kind !== 'waypoints') return false;
      const coords = decodePolyline(detail.polyline).map(([lat, lon]) => [lon, lat] as [number, number]);
      return hydrate(
        coords,
        detail.plan,
        detail.durationSeconds,
        { id: detail.id, name: detail.name, description: detail.description },
        fallbackMode,
        fallbackGravel,
      );
    },
    [hydrate],
  );

  const startFromGeometry = useCallback(
    (
      coordinates: [number, number][],
      plan: RoutePlan | null,
      durationSeconds: number | null,
      fallbackMode: SegmentMode = EMPTY.nextMode,
      fallbackGravel: RouteGravel = EMPTY.nextGravel,
    ): boolean => {
      // A roundTrip plan can't hydrate the editor — fall back to editing the line as a track.
      const usable = plan && plan.kind === 'waypoints' ? plan : null;
      return hydrate(coordinates, usable, durationSeconds, null, fallbackMode, fallbackGravel);
    },
    [hydrate],
  );

  const stop = useCallback(() => {
    abortAll();
    undoRef.current = [];
    setModel(EMPTY);
  }, [abortAll, setModel]);

  const addWaypoint = useCallback(
    (lat: number, lon: number, name?: string) => {
      const m = modelRef.current;
      if (!m.active || m.waypoints.length >= MAX_WAYPOINTS) return;
      pushUndo();
      const wp: EditorWaypoint = { id: crypto.randomUUID(), lat, lon, name };
      if (m.waypoints.length === 0) {
        setModel({ ...m, waypoints: [wp], dirty: true, selection: null });
        return;
      }
      const from = m.waypoints[m.waypoints.length - 1];
      const segment = makeSegment(crypto.randomUUID(), from, wp, m.nextMode, m.nextGravel);
      const waypoints = [...m.waypoints, wp];
      const segments = [...m.segments, segment];
      setModel({ ...m, waypoints, segments, dirty: true, selection: null });
      routePending(waypoints, segments, [segment.id]);
    },
    [makeSegment, pushUndo, routePending, setModel],
  );

  const moveWaypoint = useCallback(
    (id: string, lat: number, lon: number) => {
      const m = modelRef.current;
      const index = m.waypoints.findIndex((w) => w.id === id);
      if (!m.active || index < 0) return;
      pushUndo();
      // A dragged waypoint is no longer the searched place — drop the stale name.
      const moved: EditorWaypoint = { ...m.waypoints[index], lat, lon, name: undefined };
      const waypoints = m.waypoints.map((w) => (w.id === id ? moved : w));
      // Minimal recalc: only the segments touching this waypoint (i−1 and i). A track's
      // verbatim line can't survive a moved endpoint, so it converts to a routed segment
      // (the next-segment chips' mode) — the deliberate "re-route this stretch" gesture.
      const touched: string[] = [];
      const segments = m.segments.map((segment, si) => {
        if (si !== index - 1 && si !== index) return segment;
        touched.push(segment.id);
        const mode = segment.mode === 'track' ? m.nextMode : segment.mode;
        const gravel = segment.mode === 'track' ? m.nextGravel : segment.gravel;
        return makeSegment(segment.id, waypoints[si], waypoints[si + 1], mode, gravel);
      });
      setModel({ ...m, waypoints, segments, dirty: true });
      routePending(waypoints, segments, touched);
    },
    [makeSegment, pushUndo, routePending, setModel],
  );

  const dropTracking = useCallback((segmentId: string) => {
    abortRef.current.get(segmentId)?.abort();
    abortRef.current.delete(segmentId);
    tokenRef.current.delete(segmentId);
  }, []);

  const removeWaypoint = useCallback(
    (id: string) => {
      const m = modelRef.current;
      const index = m.waypoints.findIndex((w) => w.id === id);
      if (!m.active || index < 0) return;
      pushUndo();
      const waypoints = m.waypoints.filter((w) => w.id !== id);
      let segments: EditorSegment[];
      const touched: string[] = [];
      if (m.segments.length === 0) {
        segments = [];
      } else if (index === 0) {
        dropTracking(m.segments[0].id);
        segments = m.segments.slice(1);
      } else if (index === m.waypoints.length - 1) {
        dropTracking(m.segments[m.segments.length - 1].id);
        segments = m.segments.slice(0, -1);
      } else {
        // Merge the two adjacent segments into one, keeping the earlier one's identity
        // and mode/gravel (plan rule), spanning the removed waypoint's neighbours.
        const earlier = m.segments[index - 1];
        const later = m.segments[index];
        dropTracking(later.id);
        let merged: EditorSegment;
        if (earlier.mode === 'track' && later.mode === 'track') {
          // Two track halves share their joint (the removed waypoint) — concatenating
          // them just un-splits the original line. Zero routing, geometry unchanged.
          const coords = [...earlier.coords, ...later.coords.slice(1)];
          merged = {
            ...earlier,
            coords,
            distanceMeters: earlier.distanceMeters + later.distanceMeters,
            durationMillis:
              earlier.durationMillis != null && later.durationMillis != null
                ? earlier.durationMillis + later.durationMillis
                : null,
          };
        } else {
          // A track merged with anything else loses its verbatim line — route the span.
          const mode = earlier.mode === 'track' ? m.nextMode : earlier.mode;
          const gravel = earlier.mode === 'track' ? m.nextGravel : earlier.gravel;
          merged = makeSegment(earlier.id, waypoints[index - 1], waypoints[index], mode, gravel);
          touched.push(merged.id);
        }
        segments = [...m.segments.slice(0, index - 1), merged, ...m.segments.slice(index + 1)];
      }
      setModel({ ...m, waypoints, segments, dirty: true, selection: null });
      routePending(waypoints, segments, touched);
    },
    [dropTracking, makeSegment, pushUndo, routePending, setModel],
  );

  /** Splits a segment at an explicit point into two (both inherit mode/gravel), selecting
   *  the new waypoint. Backs both the midpoint "+ Via here" button and drag-on-line.
   *  Track segments split at the nearest polyline vertex instead of routing — the line
   *  itself doesn't move, the split just makes that point a draggable via. */
  const insertViaAt = useCallback(
    (segmentId: string, lat: number, lon: number) => {
      const m = modelRef.current;
      const index = m.segments.findIndex((s) => s.id === segmentId);
      if (!m.active || index < 0 || m.waypoints.length >= MAX_WAYPOINTS) return;
      const segment = m.segments[index];

      if (segment.mode === 'track') {
        // Interior vertex nearest the tap; a 2-point track has none, so the tapped point
        // itself becomes a vertex (the only case where a split adds a coordinate).
        const at = Math.min(Math.max(nearestVertexIndex(segment.coords, lat, lon), 1), segment.coords.length - 2);
        const twoPoint = segment.coords.length === 2;
        const joint: [number, number] = twoPoint ? [lon, lat] : segment.coords[at];
        const firstCoords = twoPoint ? [segment.coords[0], joint] : segment.coords.slice(0, at + 1);
        const secondCoords = twoPoint ? [joint, segment.coords[1]] : segment.coords.slice(at);
        pushUndo();
        const wp: EditorWaypoint = { id: crypto.randomUUID(), lat: joint[1], lon: joint[0] };
        const waypoints = [...m.waypoints.slice(0, index + 1), wp, ...m.waypoints.slice(index + 1)];
        const firstMeters = lineMeters(firstCoords);
        const secondMeters = lineMeters(secondCoords);
        const total = firstMeters + secondMeters;
        const share = (meters: number) =>
          segment.durationMillis != null && total > 0 ? (segment.durationMillis * meters) / total : null;
        const half = (id: string, coords: [number, number][], meters: number): EditorSegment => ({
          id,
          mode: 'track',
          gravel: segment.gravel,
          coords,
          distanceMeters: meters,
          durationMillis: share(meters),
          status: 'ok',
        });
        const segments = [
          ...m.segments.slice(0, index),
          half(crypto.randomUUID(), firstCoords, firstMeters),
          half(crypto.randomUUID(), secondCoords, secondMeters),
          ...m.segments.slice(index + 1),
        ];
        setModel({ ...m, waypoints, segments, dirty: true, selection: { kind: 'waypoint', id: wp.id } });
        return;
      }

      pushUndo();
      const wp: EditorWaypoint = { id: crypto.randomUUID(), lat, lon };
      const waypoints = [...m.waypoints.slice(0, index + 1), wp, ...m.waypoints.slice(index + 1)];
      dropTracking(segment.id);
      const first = makeSegment(crypto.randomUUID(), waypoints[index], wp, segment.mode, segment.gravel);
      const second = makeSegment(crypto.randomUUID(), wp, waypoints[index + 2], segment.mode, segment.gravel);
      const segments = [...m.segments.slice(0, index), first, second, ...m.segments.slice(index + 1)];
      setModel({ ...m, waypoints, segments, dirty: true, selection: { kind: 'waypoint', id: wp.id } });
      routePending(waypoints, segments, [first.id, second.id]);
    },
    [dropTracking, makeSegment, pushUndo, routePending, setModel],
  );

  const insertVia = useCallback(
    (segmentId: string) => {
      const segment = modelRef.current.segments.find((s) => s.id === segmentId);
      if (!segment) return;
      const mid = segment.coords[Math.floor(segment.coords.length / 2)];
      insertViaAt(segmentId, mid[1], mid[0]);
    },
    [insertViaAt],
  );

  const changeSegment = useCallback(
    (segmentId: string, change: { mode?: SegmentMode; gravel?: RouteGravel }) => {
      const m = modelRef.current;
      const index = m.segments.findIndex((s) => s.id === segmentId);
      if (!m.active || index < 0) return;
      const segment = m.segments[index];
      const mode = change.mode ?? segment.mode;
      const gravel = change.gravel ?? segment.gravel;
      // Re-picking a failed segment's current mode is a retry, not a no-op.
      if (mode === segment.mode && gravel === segment.gravel && segment.status !== 'error') return;
      // A track's line comes from stored geometry — no other segment can become one.
      // (Gravel changes on a track are meaningless too: nothing gets re-routed.)
      if (mode === 'track' || (segment.mode === 'track' && change.mode === undefined)) return;
      pushUndo();
      const next = makeSegment(segment.id, m.waypoints[index], m.waypoints[index + 1], mode, gravel);
      const segments = m.segments.map((s) => (s.id === segmentId ? next : s));
      setModel({ ...m, segments, dirty: true });
      routePending(m.waypoints, segments, [next.id]);
    },
    [makeSegment, pushUndo, routePending, setModel],
  );

  const setSegmentMode = useCallback(
    (segmentId: string, mode: SegmentMode) => changeSegment(segmentId, { mode }),
    [changeSegment],
  );
  const setSegmentGravel = useCallback(
    (segmentId: string, gravel: RouteGravel) => changeSegment(segmentId, { gravel }),
    [changeSegment],
  );

  const setNextMode = useCallback(
    (mode: SegmentMode) => setModel({ ...modelRef.current, nextMode: mode }),
    [setModel],
  );
  const setNextGravel = useCallback(
    (gravel: RouteGravel) => setModel({ ...modelRef.current, nextGravel: gravel }),
    [setModel],
  );

  const retrySegment = useCallback(
    (segmentId: string) => {
      const m = modelRef.current;
      const index = m.segments.findIndex((s) => s.id === segmentId);
      if (!m.active || index < 0) return;
      const segment = m.segments[index];
      if (segment.status !== 'error' || segment.mode === 'straight' || segment.mode === 'track') return;
      const segments = m.segments.map((s) => (s.id === segmentId ? { ...s, status: 'routing' as const } : s));
      setModel({ ...m, segments });
      launchRoute(segment.id, m.waypoints[index], m.waypoints[index + 1], segment.mode, segment.gravel);
    },
    [launchRoute, setModel],
  );

  const reverse = useCallback(() => {
    const m = modelRef.current;
    if (!m.active || m.waypoints.length < 2) return;
    pushUndo();
    const waypoints = [...m.waypoints].reverse();
    // Straight/track lines survive a flip verbatim; routed segments can't (one-way
    // streets, roundabout approaches), so every routed one re-routes — reversing is the
    // one edit that legitimately touches everything.
    const touched: string[] = [];
    const segments = [...m.segments].reverse().map((segment, i) => {
      if (segment.mode === 'straight' || segment.mode === 'track') {
        return { ...segment, coords: [...segment.coords].reverse() };
      }
      touched.push(segment.id);
      return makeSegment(segment.id, waypoints[i], waypoints[i + 1], segment.mode, segment.gravel);
    });
    setModel({ ...m, waypoints, segments, dirty: true, selection: null });
    routePending(waypoints, segments, touched);
  }, [makeSegment, pushUndo, routePending, setModel]);

  const select = useCallback(
    (selection: EditorSelection) => setModel({ ...modelRef.current, selection }),
    [setModel],
  );

  const undo = useCallback(() => {
    const snapshot = undoRef.current.pop();
    if (!snapshot) return;
    abortAll();
    const m = modelRef.current;
    setModel({ ...m, waypoints: snapshot.waypoints, segments: snapshot.segments, selection: null, dirty: true });
    // A snapshot can hold segments captured mid-flight (or failed) — re-fire those.
    snapshot.segments.forEach((segment, i) => {
      if (segment.status === 'ok' || segment.mode === 'straight' || segment.mode === 'track') return;
      launchRoute(segment.id, snapshot.waypoints[i], snapshot.waypoints[i + 1], segment.mode, segment.gravel);
    });
  }, [abortAll, launchRoute, setModel]);

  const buildResult = useCallback((): EditorBuildResult | null => {
    const m = modelRef.current;
    if (m.waypoints.length < 2 || m.segments.length !== m.waypoints.length - 1) return null;
    if (m.segments.some((s) => s.status !== 'ok')) return null;

    // Concatenate segment geometries. Adjacent segments normally share their joint
    // coordinate (dedupe it); differing joints (the router snapped the same waypoint
    // differently per mode) keep both points, and endIndex accounts for it.
    const coordinates: [number, number][] = [...m.segments[0].coords];
    const endIndexes: number[] = [coordinates.length - 1];
    for (let i = 1; i < m.segments.length; i++) {
      const coords = m.segments[i].coords;
      const last = coordinates[coordinates.length - 1];
      const skipJoint = coords[0][0] === last[0] && coords[0][1] === last[1];
      for (let j = skipJoint ? 1 : 0; j < coords.length; j++) coordinates.push(coords[j]);
      endIndexes.push(coordinates.length - 1);
    }

    const plan: RoutePlan = {
      version: 1,
      kind: 'waypoints',
      waypoints: m.waypoints.map((w) => ({ lat: w.lat, lon: w.lon, ...(w.name ? { name: w.name } : {}) })),
      segments: m.segments.map((s, i) => ({ mode: s.mode, gravel: s.gravel, endIndex: endIndexes[i] })),
    };

    return {
      coordinates,
      plan,
      durationSeconds: m.segments.reduce((sum, s) => sum + segmentSeconds(s), 0),
      distanceMeters: m.segments.reduce((sum, s) => sum + s.distanceMeters, 0),
    };
  }, []);

  const value = useMemo<RouteEditorValue>(() => {
    const busy = model.segments.some((s) => s.status === 'routing');
    const hasError = model.segments.some((s) => s.status === 'error');
    return {
      active: model.active,
      editing: model.editing,
      waypoints: model.waypoints,
      segments: model.segments,
      selection: model.selection,
      nextMode: model.nextMode,
      nextGravel: model.nextGravel,
      dirty: model.dirty,
      canUndo: model.undoDepth > 0,
      busy,
      hasError,
      totalDistanceMeters: model.segments.reduce((sum, s) => sum + s.distanceMeters, 0),
      totalDurationSeconds: model.segments.reduce((sum, s) => sum + segmentSeconds(s), 0),
      canSave: model.active && model.waypoints.length >= 2 && !busy && !hasError,
      startNew,
      startFromRoute,
      startFromGeometry,
      stop,
      addWaypoint,
      moveWaypoint,
      removeWaypoint,
      insertVia,
      insertViaAt,
      setSegmentMode,
      setSegmentGravel,
      setNextMode,
      setNextGravel,
      retrySegment,
      reverse,
      select,
      undo,
      buildResult,
    };
  }, [
    model,
    startNew,
    startFromRoute,
    startFromGeometry,
    stop,
    addWaypoint,
    moveWaypoint,
    removeWaypoint,
    insertVia,
    insertViaAt,
    setSegmentMode,
    setSegmentGravel,
    setNextMode,
    setNextGravel,
    retrySegment,
    reverse,
    select,
    undo,
    buildResult,
  ]);

  return <RouteEditorContext.Provider value={value}>{children}</RouteEditorContext.Provider>;
};

export function useRouteEditor(): RouteEditorValue {
  const ctx = useContext(RouteEditorContext);
  if (!ctx) {
    throw new Error('useRouteEditor must be used within a RouteEditorProvider');
  }
  return ctx;
}
